import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getLocationReturnPath, useNavigateBack } from '../../../hooks/useNavigateBack';
import { useNotification } from '../../../contexts/NotificationContext';
import { createSupplyDeliveryFromPurchaseOrder } from '../../../services/mutations';
import { useSupplyForwarding } from './useData';
import { moduleConfig } from './supplyForwardingsModuleConfig';
import { moduleConfig as poModuleConfig } from '../supply-purchase-orders/supplyPurchaseOrdersModuleConfig';
import { moduleConfig as supplyDeliveriesConfig } from '../supply-deliveries/supplyDeliveriesModuleConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { Accordion, AccordionItem, Button, Card, ChevronLeftIcon, DocumentTextIcon } from '../../../components/ui';

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const numericId = id ? Number(id) : NaN;
  const { item, isLoading, isError } = useSupplyForwarding(Number.isFinite(numericId) ? numericId : undefined);
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const returnPath = React.useMemo(() => getLocationReturnPath(location.state), [location.state]);
  const goBack = useNavigateBack(moduleConfig.module.route.list, returnPath);
  const formatDate = (s?: string | null) => (s ? formatDateGlobal(s) : '');
  const fmtUsd = (v: string | number | null | undefined) => {
    if (v == null || v === '') return '—';
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) return String(v);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  };
  const [creatingDelivery, setCreatingDelivery] = React.useState(false);

  const historyEntries = React.useMemo(() => {
    const h = item?.logisticsHistory;
    if (!Array.isArray(h)) return [];
    return h as Record<string, unknown>[];
  }, [item?.logisticsHistory]);

  if (!Number.isFinite(numericId)) {
    return <p className={cn('text-sm', colors.textSecondary)}>Invalid id.</p>;
  }
  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center rounded-xl border border-dashed px-4 py-16', colors.border)}>
        <p className={cn('text-sm font-medium', colors.textSecondary)}>Loading forwarding…</p>
      </div>
    );
  }
  if (isError || !item) {
    return (
      <Card variant="outlined" className={cn('p-6 sm:p-8', colors.border, 'max-w-md')}>
        <p className={cn('text-sm', colors.textSecondary)}>This forwarding was not found or you no longer have access.</p>
        <Button type="button" variant="ghost" className="mt-4" onClick={goBack}>
          <ChevronLeftIcon className="h-4 w-4" />
          Back to list
        </Button>
      </Card>
    );
  }

  const forwarderLabel = item.forwarder
    ? item.forwarder.name
    : item.forwarderId != null
      ? `Supplier #${item.forwarderId}`
      : '—';

  const vesselId = item.supplyCase?.vessel?.id;
  const poDeliveryCount = item.purchaseOrder?._count?.deliveries ?? 0;
  const canCreateDeliveryFromForwarding =
    item.purchaseOrderId != null &&
    Number.isFinite(vesselId) &&
    vesselId! > 0 &&
    poDeliveryCount === 0;

  return (
    <div className="space-y-6">
      <Card variant="outlined" className={cn('overflow-hidden border shadow-sm', colors.border)}>
        <div
          className={cn(
            'flex flex-col gap-4 border-b px-4 py-4 sm:px-6 sm:py-5',
            colors.border,
            'bg-gradient-to-b from-slate-50/80 to-white dark:from-slate-900/40 dark:to-slate-900/20',
          )}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <Button type="button" variant="ghost" size="sm" className={cn('-ml-2 gap-1.5 self-start font-medium', colors.text)} onClick={goBack}>
                <ChevronLeftIcon className="h-4 w-4" />
                Back
              </Button>
              <h1 className={cn('mt-2 text-xl font-semibold tracking-tight sm:text-2xl', colors.text)}>
                {item.ref?.trim() ? item.ref : `Forwarding #${item.id}`}
              </h1>
              <p className={cn('mt-1 text-sm', colors.textSecondary)}>
                Case {item.supplyCase?.ref ? item.supplyCase.ref : `#${item.caseId}`}
                {item.purchaseOrder?.ref ? ` · PO ${item.purchaseOrder.ref}` : item.purchaseOrderId ? ` · PO #${item.purchaseOrderId}` : ''}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {canCreateDeliveryFromForwarding ? (
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  loading={creatingDelivery}
                  disabled={creatingDelivery}
                  className={cn(
                    'inline-flex h-9 min-h-[2.25rem] shrink-0 items-center justify-center gap-2 rounded-lg border px-4 text-sm font-semibold shadow-sm',
                    'text-emerald-800 hover:bg-emerald-50 dark:text-emerald-200 dark:hover:bg-emerald-900/30',
                    colors.border,
                  )}
                  title="Create vessel delivery from this purchase order (same as case view)"
                  onClick={async () => {
                    if (!canCreateDeliveryFromForwarding || item.purchaseOrderId == null || vesselId == null) return;
                    setCreatingDelivery(true);
                    try {
                      const created = await createSupplyDeliveryFromPurchaseOrder({
                        purchaseOrderId: item.purchaseOrderId,
                        vesselId,
                        supplyForwardingId: item.id,
                      });
                      showSuccess('Delivery created.');
                      const dId = (created as { id?: number })?.id;
                      if (dId) navigate(supplyDeliveriesConfig.module.route.edit(dId));
                    } catch (err: unknown) {
                      const data =
                        err && typeof err === 'object' && 'response' in err
                          ? (err as { response?: { data?: { message?: string | string[] } } }).response?.data
                          : undefined;
                      const m = data?.message;
                      showError(
                        Array.isArray(m) ? m.join(' ') : typeof m === 'string' ? m : 'Could not create delivery.',
                      );
                    } finally {
                      setCreatingDelivery(false);
                    }
                  }}
                >
                  <DocumentTextIcon className="h-4 w-4 shrink-0" />
                  Create delivery
                </Button>
              ) : item.purchaseOrderId != null && poDeliveryCount > 0 ? (
                <span className={cn('inline-flex h-9 items-center px-1 text-sm', colors.textSecondary)} title="A delivery already exists for this order">
                  Delivery exists
                </span>
              ) : item.purchaseOrderId == null ? (
                <span
                  className={cn('inline-flex h-9 items-center px-1 text-sm', colors.textSecondary)}
                  title="Link a purchase order on the forwarding to create a delivery from it"
                >
                  No PO on forwarding
                </span>
              ) : (
                <span
                  className={cn('inline-flex h-9 items-center px-1 text-sm', colors.textSecondary)}
                  title="Supply case has no vessel — set vessel on the case before creating a delivery"
                >
                  Case has no vessel
                </span>
              )}
              <Link
                to={moduleConfig.module.route.edit(item.id)}
                state={location.state}
                className={cn(
                  'inline-flex h-9 min-h-[2.25rem] shrink-0 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold shadow-sm transition-all hover:opacity-95',
                  colors.primary,
                  colors.primaryText,
                )}
              >
                Edit
              </Link>
            </div>
          </div>
        </div>
        <div className={cn('space-y-6 px-4 py-5 sm:px-6', colors.text)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Status</p>
              <p className="mt-1 text-sm">
                {item.status ? item.status.name : item.statusId != null ? `#${item.statusId}` : '—'}
              </p>
            </div>
            <div>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>ETA to port</p>
              <p className="mt-1 text-sm">{formatDate(item.etaToPort)}</p>
            </div>
            <div>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Port</p>
              <p className="mt-1 text-sm">
                {item.port ? (item.port.name) : '—'}
              </p>
            </div>
            <div>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Forwarder</p>
              <p className="mt-1 text-sm">{forwarderLabel}</p>
            </div>
            <div>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Priority</p>
              <p className="mt-1 text-sm">
                {item.priority
                  ? item.priority.name
                  : '—'}
              </p>
            </div>
            <div>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Cost (USD)</p>
              <p className="mt-1 text-sm tabular-nums">{fmtUsd(item.costUsd)}</p>
            </div>
            <div>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Warehouse location</p>
              <p className="mt-1 text-sm">
                {item.warehouseLocation
                  ? item.warehouseLocation.name
                  : '—'}
              </p>
            </div>
            <div>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Packages</p>
              <p className="mt-1 text-sm">{item.packageCount != null ? String(item.packageCount) : '—'}</p>
            </div>
            <div>
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Total weight</p>
              <p className="mt-1 text-sm">{item.totalWeight != null && item.totalWeight !== '' ? String(item.totalWeight) : '—'}</p>
            </div>
            <div className="sm:col-span-2">
              <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Notes</p>
              <p className={cn('mt-1 whitespace-pre-wrap text-sm', colors.textSecondary)}>{item.notes?.trim() ? item.notes : '—'}</p>
            </div>
            {item.purchaseOrderId ? (
              <div className="sm:col-span-2">
                <Link
                  to={poModuleConfig.module.route.detail(item.purchaseOrderId)}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  Open purchase order
                </Link>
              </div>
            ) : null}
          </div>

          <Accordion className="w-full">
            <AccordionItem
              title="Warehouse (WH)"
              description="Arrival, departure, invoice, cost (USD)."
              defaultOpen={false}
              className={cn('overflow-hidden', colors.border, 'border')}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Arrival</p>
                  <p className="mt-1 text-sm">{formatDate(item.warehouseArrivalDate)}</p>
                </div>
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Departure</p>
                  <p className="mt-1 text-sm">{formatDate(item.warehouseDepartureDate)}</p>
                </div>
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Invoice no.</p>
                  <p className="mt-1 text-sm">{item.warehouseInvoiceNo?.trim() ? item.warehouseInvoiceNo : '—'}</p>
                </div>
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Cost (USD)</p>
                  <p className="mt-1 text-sm tabular-nums">{fmtUsd(item.warehouseCostUsd)}</p>
                </div>
              </div>
            </AccordionItem>
            <AccordionItem
              title="Sub-agent (SA)"
              description="Arrival, delivery, invoice, cost (USD)."
              defaultOpen={false}
              className={cn('mt-3 overflow-hidden', colors.border, 'border')}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Arrival</p>
                  <p className="mt-1 text-sm">{formatDate(item.subAgentArrivalDate)}</p>
                </div>
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Delivery</p>
                  <p className="mt-1 text-sm">{formatDate(item.subAgentDeliveryDate)}</p>
                </div>
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Invoice no.</p>
                  <p className="mt-1 text-sm">{item.subAgentInvoiceNo?.trim() ? item.subAgentInvoiceNo : '—'}</p>
                </div>
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Cost (USD)</p>
                  <p className="mt-1 text-sm tabular-nums">{fmtUsd(item.subAgentCostUsd)}</p>
                </div>
              </div>
            </AccordionItem>
            <AccordionItem
              title="Vessel agent (VA)"
              description="Arrival, delivery, invoice, cost (USD)."
              defaultOpen={false}
              className={cn('mt-3 overflow-hidden', colors.border, 'border')}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Arrival</p>
                  <p className="mt-1 text-sm">{formatDate(item.vesselAgentArrivalDate)}</p>
                </div>
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Delivery</p>
                  <p className="mt-1 text-sm">{formatDate(item.vesselAgentDeliveryDate)}</p>
                </div>
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Invoice no.</p>
                  <p className="mt-1 text-sm">{item.vesselAgentInvoiceNo?.trim() ? item.vesselAgentInvoiceNo : '—'}</p>
                </div>
                <div>
                  <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Cost (USD)</p>
                  <p className="mt-1 text-sm tabular-nums">{fmtUsd(item.vesselAgentCostUsd)}</p>
                </div>
              </div>
            </AccordionItem>
          </Accordion>

          {historyEntries.length > 0 ? (
            <Accordion className="w-full">
              <AccordionItem
                title="Port / ETA change log"
                description={`${historyEntries.length} entr${historyEntries.length === 1 ? 'y' : 'ies'}`}
                defaultOpen={false}
                className={cn('overflow-hidden', colors.border, 'border')}
              >
                <div className="max-h-72 overflow-auto rounded-lg border border-slate-200/80 dark:border-slate-700/80">
                  <table className="w-full min-w-[640px] text-left text-xs">
                    <thead className={cn('sticky top-0', colors.border, 'border-b bg-slate-50/95 dark:bg-slate-900/95')}>
                      <tr>
                        <th className="px-2 py-2 font-medium">At</th>
                        <th className="px-2 py-2 font-medium">Port</th>
                        <th className="px-2 py-2 font-medium">ETA</th>
                        <th className="px-2 py-2 font-medium">By user</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyEntries.map((row, i) => (
                        <tr key={i} className={cn('border-b last:border-0', colors.border)}>
                          <td className="px-2 py-1.5 tabular-nums">{String(row.at ?? '')}</td>
                          <td className="px-2 py-1.5">
                            {String(row.oldPortId ?? '—')} → {String(row.newPortId ?? '—')}
                          </td>
                          <td className="px-2 py-1.5">
                            {String(row.oldEtaToPort ?? '—')} → {String(row.newEtaToPort ?? '—')}
                          </td>
                          <td className="px-2 py-1.5">{row.updatedByUserId != null ? String(row.updatedByUserId) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AccordionItem>
            </Accordion>
          ) : null}
        </div>
      </Card>
    </div>
  );
};

export default Detail;
